import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Check, X, FileSpreadsheet, MessageCircle, Mail, PhoneCall, FolderX,
  LayoutDashboard, Banknote, TrendingUp, Percent, Handshake, CalendarClock, UserCheck, BarChart3,
  Building2, Landmark, Stethoscope, GraduationCap, Factory, Truck, HardHat, Radio, Car, Coins,
  Home, Plane, Store, Users, ClipboardList, Scale, History, ShieldCheck, ScrollText, Upload, Download,
  Layers, ChevronRight, Gift, Loader2, CalendarCheck, MapPin, Building, Menu, ChevronDown, Facebook, Instagram, Linkedin, Youtube,
} from "lucide-react";
import { LeadDialog } from "@/components/landing/LeadDialog";
import { Brand } from "@/components/Brand";
import api from "@/lib/api";
import { goToApp } from "@/lib/domains";
import { SecureBadge } from "@/components/legal/LegalShell";
import { RazorpaySecureBar, TrustSection } from "@/components/PaymentTrust";
import { WelcomeModal } from "@/components/WelcomeModal";
import { FloatingDemoButton } from "@/components/FloatingDemoButton";
import { WatchDemoModal } from "@/components/WatchDemoModal";
import { DemoThumbnail } from "@/components/DemoThumbnail";
import { AiRibbon } from "@/components/landing/AiRibbon";
import { HeroBackdrop } from "@/components/landing/HeroBackdrop";
import { LivingDashboard } from "@/components/landing/LivingDashboard";
import { Sparkles } from "lucide-react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">{children}</span>
);

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#0B3D91] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${idx}`}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const INDUSTRIES = [
  { icon: Landmark, label: "NBFCs" },
  { icon: Handshake, label: "Collection Agencies" },
  { icon: Stethoscope, label: "Hospitals" },
  { icon: GraduationCap, label: "Schools & Colleges" },
  { icon: Factory, label: "Manufacturers" },
  { icon: Truck, label: "Distributors" },
  { icon: HardHat, label: "Builders" },
  { icon: Radio, label: "Telecom Providers" },
  { icon: Car, label: "Motor Finance" },
  { icon: Coins, label: "Gold Loan Companies" },
  { icon: Home, label: "RWAs / Societies" },
  { icon: Plane, label: "Tour & Travel" },
  { icon: Store, label: "Showrooms & Retail" },
];

const FEATURES = [
  { icon: ClipboardList, label: "Account Management" },
  { icon: CalendarClock, label: "Follow-Up Tracking" },
  { icon: Handshake, label: "Settlement Workflow" },
  { icon: Banknote, label: "Promise To Pay Tracking" },
  { icon: TrendingUp, label: "Payment Tracking" },
  { icon: Scale, label: "Legal Escalation Tracking" },
  { icon: UserCheck, label: "User Assignment" },
  { icon: History, label: "Activity Timeline" },
  { icon: BarChart3, label: "Reports & Analytics" },
  { icon: ShieldCheck, label: "Role Based Access" },
  { icon: ScrollText, label: "Audit Logs" },
  { icon: Upload, label: "Excel Import" },
  { icon: Download, label: "Excel Export" },
  { icon: Layers, label: "Multi-Tenant Architecture" },
];

const WORKFLOW = ["Account", "Follow-Up", "Communication", "Promise To Pay", "Settlement", "Payment", "Legal Action", "Closure"];

const PAINS = [
  { icon: FileSpreadsheet, label: "Excel Sheets" },
  { icon: MessageCircle, label: "WhatsApp Messages" },
  { icon: Mail, label: "Scattered Emails" },
  { icon: PhoneCall, label: "Manual Follow-Ups" },
  { icon: FolderX, label: "Scattered Records" },
];

const WORKFLOW_STEPS = [
  { n: 1, icon: Upload, eyebrow: "Step 1 · Setup", title: "Upload Excel or add customers", desc: (<>Import your Excel or simply add customers using <span className="font-semibold text-slate-800">Customer ID</span>, <span className="font-semibold text-slate-800">Customer Name</span>, <span className="font-semibold text-slate-800">Amount Due</span> and <span className="font-semibold text-slate-800">Due Date</span>. RecoverOS maps everything automatically.</>) },
  { n: 2, icon: Mail, eyebrow: "Step 2 · Automate", title: "Automatic email follow-ups", desc: "Every overdue customer receives scheduled reminders automatically. No manual chasing." },
  { n: 3, icon: TrendingUp, green: true, eyebrow: "Step 3 · The Payoff", title: "Faster cash collection", desc: "Consistent email follow-ups improve customer response and help accelerate collections." },
  { n: 4, icon: LayoutDashboard, eyebrow: "Step 4 · Stay Ahead", title: "Know exactly what to follow up next", desc: (<><span className="font-semibold text-slate-800">Money Stuck Analyzer™</span> shows where your cash is stuck and <span className="font-semibold text-slate-800">Recovery Health Score</span> tracks progress — with daily priorities telling your team exactly who to chase, all in one dashboard.</>) },
];


const DASH_CARDS = [
  { icon: Banknote, label: "Total Receivables", value: "₹4.82 Cr", tint: "#0B3D91" },
  { icon: TrendingUp, label: "Total Outstanding", value: "₹1.96 Cr", tint: "#0B3D91" },
  { icon: Check, label: "Total Recovered", value: "₹2.86 Cr", tint: "#16A34A" },
  { icon: Percent, label: "Recovery %", value: "59.3%", tint: "#16A34A" },
  { icon: Handshake, label: "Settlement Pipeline", value: "₹64.2 L", tint: "#0B3D91" },
  { icon: CalendarClock, label: "Follow-Ups Due", value: "138", tint: "#0B3D91" },
  { icon: UserCheck, label: "Accounts Assigned", value: "2,410", tint: "#0B3D91" },
  { icon: BarChart3, label: "Collections Performance", value: "+12.4%", tint: "#16A34A" },
];

const PLAN_ASSURANCES = ["Secure Payments by Razorpay", "GST Invoice Included", "Instant Plan Activation", "Upgrade Anytime", "No Hidden Charges", "Cancel Anytime"];

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const PARTNER_AUDIENCE = ["Accountants", "CA Firms", "Loan DSAs", "Collection Consultants", "Business Consultants"];
const PARTNER_BENEFITS = [
  "Help Your Business Network Recover Money Faster",
  "10% First-Year Revenue Share",
  "Dedicated Partner Support",
  "No Investment Required",
];

// Benefit-led value cards (NOT testimonials). Real testimonials/logos/case studies
// can be added alongside or replace these later.
const WHY_CARDS = [
  { icon: Upload, title: "Upload Excel in Minutes", desc: "Bring your existing spreadsheet — we auto-map columns and build your portfolio instantly." },
  { icon: Layers, title: "No ERP Integration Required", desc: "Start today. No IT project, no developers, no costly integrations to get going." },
  { icon: BarChart3, title: "Money Stuck Analyzer™", desc: "See exactly how much is stuck, where it's stuck, and what to chase first." },
  { icon: Percent, title: "Recovery Health Score", desc: "A single 0–100 score that tells you how healthy your recoveries really are." },
  { icon: Building2, title: "Industry Independent Platform", desc: "NBFCs, hospitals, schools, distributors, retail — one platform adapts to all." },
  { icon: Users, title: "Multi-User Access", desc: "Invite your team with role-based access for admins, managers and agents." },
  { icon: ShieldCheck, title: "Secure Cloud Platform", desc: "SSL-secured, tenant-isolated, with role-based access and immutable audit logs." },
  { icon: CalendarClock, title: "Fast Digital Onboarding", desc: "Sign up, upload and reach your first insight in minutes — not weeks." },
];

const FAQS = [
  { q: "What is RecoverOS?", a: "RecoverOS is a Receivables, Collections & Settlement Operating System. It helps you track outstanding amounts, follow-ups, settlements and recoveries from one unified platform — purpose-built for recovery teams, not a generic CRM or ERP." },
  { q: "Who is RecoverOS for?", a: "Any business that manages receivables or collections — NBFCs, fintech lenders, collection agencies, hospitals, schools, distributors, manufacturers, builders, retailers and more." },
  { q: "Can I upload Excel files?", a: "Yes. You can upload your existing Excel (.xlsx, .xls) or CSV files during signup and anytime after. RecoverOS auto-maps your columns and builds your portfolio in minutes — a sample template is provided." },
  { q: "Which industries can use RecoverOS?", a: "RecoverOS is industry-independent. Custom fields and reports adapt the platform to your business, so the same workflow works across lending, healthcare, education, manufacturing, distribution, retail and others." },
  { q: "Is ERP integration required?", a: "No. RecoverOS works standalone — no ERP, no developers and no IT project needed. Just upload your data and start recovering." },
  { q: "Is my data secure?", a: "Yes. Your data is hosted on a secure cloud platform with SSL encryption, strict multi-tenant isolation, role-based access control and immutable audit logs. You own your data and can export it anytime." },
  { q: "What happens after the 3-month Intro PAYG period?", a: "New businesses start on Intro PAYG — ₹0 for 3 months with up to 100 accounts, 1 user and up to 250 email communications (up to 50 Free RC/month), and no subscription required. When the 3 months end, simply choose a paid plan to continue — your data and settings are preserved, and limits & features lift instantly." },
  { q: "Can multiple users access RecoverOS?", a: "Yes. You can invite your team with role-based access — admins, managers, agents and read-only auditors — with the number of users depending on your plan." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [lead, setLead] = useState({ open: false, kind: "demo", plan: "" });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const heroRef = useRef(null);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/public/pricing").then((res) => setPlans(res.data?.plans || [])).catch(() => setPlans([]));
  }, []);
  const openLead = (kind, plan = "") => setLead({ open: true, kind, plan });
  const appNav = (path) => goToApp(path, navigate);
  const NAV_LINKS = [["Industries", "industries"], ["Features", "features"], ["Pricing", "pricing"], ["FAQ", "faq"], ["Partners", "partners"]];

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={{ fontFamily: "'Inter', sans-serif" }}>
      <LeadDialog open={lead.open} onOpenChange={(o) => setLead((s) => ({ ...s, open: o }))} kind={lead.kind} plan={lead.plan} />
      <WelcomeModal onStartTrial={() => appNav("/signup")} />
      <WatchDemoModal open={watchOpen} onOpenChange={setWatchOpen} onStartTrial={() => appNav("/signup")} onBookDemo={() => openLead("demo")} source="landing-hero" />
      <FloatingDemoButton onStartTrial={() => appNav("/signup")} onBookDemo={() => openLead("demo")} source="landing-fab" />

      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between h-16">
          <a href="#top" className="flex items-center gap-2 ros-logo-float" data-testid="brand-logo">
            <Brand iconSize={32} textClass="text-lg" style={JK} />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(([t, id]) => (
              id === "pricing"
                ? <button key={id} onClick={() => navigate("/pricing")} className="text-sm font-medium text-slate-600 hover:text-slate-950 transition-colors" data-testid={`nav-${id}`}>{t}</button>
                : <a key={id} href={`#${id}`} className="text-sm font-medium text-slate-600 hover:text-slate-950 transition-colors" data-testid={`nav-${id}`}>{t}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => appNav("/signin")} className="hidden sm:inline-flex text-sm font-semibold text-slate-700 hover:text-[#0B3D91] transition-colors px-3 py-2" data-testid="nav-login">Login</button>
            <button onClick={() => openLead("demo")} className="hidden sm:inline-flex border-2 border-[#0B3D91] text-[#0B3D91] bg-transparent rounded-none px-4 py-2.5 text-sm font-semibold hover:bg-[#0B3D91]/[0.05] transition-colors" data-testid="nav-demo">Book Demo</button>
            <button onClick={() => appNav("/signup")} className="hidden sm:inline-flex bg-[#0B3D91] text-white rounded-none px-4 py-2.5 text-sm font-semibold hover:bg-[#0a347d] transition-colors" data-testid="nav-trial">Get Started</button>
            <button onClick={() => setMobileOpen((o) => !o)} className="md:hidden inline-flex items-center justify-center h-10 w-10 text-slate-800 hover:text-[#0B3D91] transition-colors" data-testid="mobile-menu-toggle" aria-label="Toggle menu" aria-expanded={mobileOpen}>
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="md:hidden overflow-hidden border-t border-slate-200 bg-white"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col px-6 py-4 gap-1">
                {NAV_LINKS.map(([t, id]) => (
                  id === "pricing"
                    ? <button key={id} onClick={() => { setMobileOpen(false); navigate("/pricing"); }} className="py-3 text-left text-base font-medium text-slate-700 hover:text-[#0B3D91] border-b border-slate-100 transition-colors" data-testid={`mobile-nav-${id}`}>{t}</button>
                    : <a key={id} href={`#${id}`} onClick={() => setMobileOpen(false)} className="py-3 text-base font-medium text-slate-700 hover:text-[#0B3D91] border-b border-slate-100 transition-colors" data-testid={`mobile-nav-${id}`}>{t}</a>
                ))}
                <div className="flex flex-col gap-2 pt-4">
                  <button onClick={() => { setMobileOpen(false); appNav("/signin"); }} className="w-full border-2 border-slate-300 text-slate-900 rounded-none px-4 py-3 text-sm font-semibold hover:border-[#0B3D91] hover:text-[#0B3D91] transition-colors" data-testid="mobile-nav-login">Login</button>
                  <button onClick={() => { setMobileOpen(false); openLead("demo"); }} className="w-full border-2 border-[#0B3D91] text-[#0B3D91] bg-transparent rounded-none px-4 py-3 text-sm font-semibold hover:bg-[#0B3D91]/[0.05] transition-colors" data-testid="mobile-nav-demo">Book Demo</button>
                  <button onClick={() => { setMobileOpen(false); appNav("/signup"); }} className="w-full bg-[#16A34A] text-white rounded-none px-4 py-3 text-sm font-semibold hover:bg-[#138a3e] transition-colors" data-testid="mobile-nav-trial">Get Started</button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
        <AiRibbon />
      </header>

      {/* HERO */}
      <section id="top" ref={heroRef} className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <HeroBackdrop targetRef={heroRef} />
        <div className="relative z-[3] mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#2563eb]/20 bg-white/70 px-3.5 py-1.5 text-xs font-bold text-[#0B3D91] backdrop-blur" data-testid="hero-ai-badge">
              <Sparkles className="h-3.5 w-3.5 text-[#0ea5a4]" /> AI-powered receivables intelligence
            </span>
            <span className="mt-4 block text-xs font-bold uppercase tracking-[0.18em] text-[#0B3D91]" data-testid="hero-eyebrow">B2B Receivables Management Software</span>
            <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-950" style={JK}>
              Your Customers Owe You Money. RecoverOS Helps You <span className="text-[#16A34A]">Collect It Faster.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-slate-600 max-w-xl">
              RecoverOS helps businesses automate email follow-ups, track outstanding payments, monitor recovery performance, and know exactly which customers need attention—all from one intelligent dashboard.
            </p>
            <div className="mt-8 flex flex-wrap items-start gap-4">
              <div className="flex flex-col">
                <button onClick={() => appNav("/signup")} className="group bg-[#16A34A] text-white rounded-none px-6 py-3.5 font-semibold hover:bg-[#138a3e] hover:-translate-y-0.5 transition-all flex items-center gap-2" data-testid="hero-trial">
                  Start Free — Intro PAYG <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <span className="mt-2 text-xs text-slate-500">₹0 Intro PAYG • 3 Months • Up to 250 Email Follow-ups Included</span>
              </div>
              <div className="flex flex-col">
                <button onClick={() => openLead("demo")} className="group border-2 border-[#0B3D91] bg-white text-[#0B3D91] rounded-none px-6 py-3.5 font-semibold hover:bg-[#0B3D91]/[0.04] hover:-translate-y-0.5 transition-all flex items-center gap-2" data-testid="hero-demo">
                  Book Live Demo <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <span className="mt-2 text-xs text-slate-500">30-min live walkthrough</span>
              </div>
            </div>
            <div className="mt-4">
              <button onClick={() => setWatchOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B3D91] hover:underline" data-testid="hero-watch-demo">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0B3D91]/10">▶</span> Watch the 4-min demo
              </button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-slate-600" data-testid="hero-feature-checks">
              {["Upload Excel in Minutes", "Automatic Email Follow-ups", "Money Stuck Analyzer™", "Recovery Health Score™"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#16A34A]" /> {t}</span>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-200 max-w-xl text-sm font-semibold text-slate-600" data-testid="hero-trust">
              Built for <span className="text-[#0B3D91]">NBFCs</span> • <span className="text-[#0B3D91]">Finance Companies</span> • <span className="text-[#0B3D91]">Retail</span> • <span className="text-[#0B3D91]">Hospitals</span> • <span className="text-[#0B3D91]">Education</span> • <span className="text-[#0B3D91]">Manufacturers</span> • <span className="text-[#0B3D91]">Distributors</span>
            </div>
          </FadeUp>

          {/* Living Portfolio Intelligence panel */}
          <FadeUp delay={0.15}>
            <LivingDashboard />
          </FadeUp>
        </div>
      </section>

      {/* THE RECOVERY WORKFLOW — Upload → Automate → Recover → Measure */}
      <section className="py-20 lg:py-24 bg-white border-b border-slate-200" data-testid="workflow-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="max-w-3xl">
            <Overline>The Recovery Workflow</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950 leading-[1.08]" style={JK}>
              Turn Overdue Payments Into Cash — Faster
            </h2>
            <p className="mt-5 text-base lg:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Upload your Excel or add customers manually. RecoverOS automatically sends email follow-ups, tracks responses, and shows every recovery in one intelligent dashboard. You're ready in under 5 minutes.
            </p>
            <div className="mt-6 flex items-start gap-3 border-l-2 border-[#16A34A] bg-slate-50 px-4 py-3 max-w-2xl">
              <span className="text-sm font-medium text-slate-700">No implementation. No ERP integration. Just upload your customer list and RecoverOS starts working.</span>
            </div>
          </FadeUp>

          <div className="mt-14 relative">
            {/* connecting rail (desktop) */}
            <div className="hidden lg:block absolute top-4 left-[12.5%] right-[12.5%] h-[2px] bg-slate-200" />
            {/* directional arrows on the rail */}
            {[
              { left: "left-1/4", cls: "border-slate-200 text-[#0B3D91]", d: 0.25 },
              { left: "left-1/2", cls: "border-[#16A34A] text-[#16A34A]", d: 0.45 },
              { left: "left-3/4", cls: "border-slate-200 text-[#0B3D91]", d: 0.65 },
            ].map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: a.d }}
                className={`hidden lg:flex absolute top-4 ${a.left} -translate-x-1/2 -translate-y-1/2 h-7 w-7 items-center justify-center bg-white border ${a.cls} z-10`}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.div>
            ))}

            <div className="grid gap-8 lg:gap-8 lg:grid-cols-4 relative items-stretch">
              {WORKFLOW_STEPS.map((s, i) => {
                const green = s.green;
                return (
                  <FadeUp key={s.n} delay={i * 0.12} className="flex flex-col">
                    <div className="flex justify-center relative z-10">
                      <span className={`flex ${green ? "h-9 w-9" : "h-8 w-8"} items-center justify-center ${green ? "bg-[#16A34A]" : "bg-[#0B3D91]"} text-white text-sm font-bold ring-4 ring-white`} style={JK}>{s.n}</span>
                    </div>
                    <div className={`mt-6 p-6 lg:p-7 h-full ${green ? "bg-[#16A34A]/[0.06] border-2 border-[#16A34A]/50 shadow-[0_2px_0_0_rgba(22,163,74,0.18)]" : "bg-white border border-slate-200"}`}>
                      <div className={`${green ? "h-14 w-14 border-[#16A34A]/50 text-[#16A34A]" : "h-12 w-12 border-slate-200 text-[#0B3D91]"} border bg-white flex items-center justify-center`}>
                        <s.icon className={green ? "h-7 w-7" : "h-6 w-6"} />
                      </div>
                      <span className={`mt-5 block text-[11px] font-bold uppercase tracking-[0.16em] ${green ? "text-[#16A34A]" : "text-slate-400"}`}>{s.eyebrow}</span>
                      <h3 className={`mt-1.5 ${green ? "text-xl" : "text-lg"} font-extrabold text-slate-950`} style={JK}>{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                      {green && (
                        <div className="mt-4 inline-flex items-center gap-2 border border-[#16A34A]/40 bg-white px-3 py-2">
                          <TrendingUp className="h-4 w-4 text-[#16A34A]" />
                          <span className="text-xs font-bold text-[#16A34A]">Recovery trending up</span>
                        </div>
                      )}
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>

          <FadeUp delay={0.1}>
            <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <button onClick={() => appNav("/signup")} className="group bg-[#16A34A] text-white rounded-none px-9 py-4 font-bold text-base hover:bg-[#138a3e] hover:-translate-y-0.5 transition-all flex items-center gap-2.5 shadow-sm" data-testid="workflow-cta">
                Start Free — Add Your Customers <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <span className="text-sm text-slate-500"><span className="font-bold text-slate-700">₹0 Intro PAYG</span> • 3 Months • Up to 250 Email Follow-ups Included • No Credit Card Required</span>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-slate-500">
              {["Works with your existing Excel", "No implementation project", "Setup in under 5 minutes", "Start sending today"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#16A34A]" /> {t}</span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>The Problem</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Stop Managing Recoveries On Excel</h2>
          </FadeUp>
          <div className="mt-14 grid lg:grid-cols-[1fr_auto_auto] gap-8 items-center">
            <FadeUp className="grid sm:grid-cols-2 gap-4">
              {PAINS.map((p) => (
                <div key={p.label} className="flex items-center gap-3 border border-slate-200 bg-slate-50 px-5 py-4">
                  <X className="h-5 w-5 text-red-500 shrink-0" />
                  <p.icon className="h-5 w-5 text-slate-400 shrink-0" />
                  <span className="font-medium text-slate-700">{p.label}</span>
                </div>
              ))}
            </FadeUp>
            <FadeUp delay={0.1} className="hidden lg:flex justify-center">
              <ArrowRight className="h-10 w-10 text-[#0B3D91]" />
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="bg-[#0B3D91] text-white p-8 lg:p-10 text-center min-w-[260px]">
                <Layers className="h-8 w-8 mx-auto" />
                <div className="mt-4 text-2xl font-extrabold" style={JK}>RecoverOS</div>
                <div className="mt-2 text-sm text-blue-100">Single Source Of Truth</div>
                <div className="mt-4 inline-flex items-center gap-2 text-[#a7f3c4] text-sm font-semibold"><Check className="h-4 w-4" /> One platform. One workflow.</div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* DASHBOARD SECTION */}
      <section className="py-20 lg:py-28 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Command Center</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950 max-w-2xl" style={JK}>Every recovery metric, in one dashboard</h2>
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DASH_CARDS.map((c, i) => (
              <FadeUp key={c.label} delay={i * 0.04}>
                <div className="ros-feat rounded-xl border border-slate-200 bg-white p-6 h-full">
                  <div className="flex items-center justify-between">
                    <c.icon className="h-6 w-6" style={{ color: c.tint }} />
                    <span className="text-3xl font-extrabold tracking-tight" style={{ ...JK, color: c.tint }}>{c.value}</span>
                  </div>
                  <div className="mt-4 text-sm font-medium text-slate-600">{c.label}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WATCH PRODUCT DEMO */}
      <section className="py-20 lg:py-28 bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-center">
          <DemoThumbnail
            eyebrow="Product Demo"
            title="Watch Product Demo"
            subtitle="A 4-minute walkthrough of how recovery teams go from scattered Excel to a single source of truth."
            onStartTrial={() => appNav("/signup")}
            onBookDemo={() => openLead("demo")}
            source="landing-section"
            className="mx-auto"
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>How It Works</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>One Workflow. Any Industry.</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">From the first follow-up to final closure — RecoverOS guides every account through a structured recovery lifecycle.</p>
          </FadeUp>
          <div className="mt-14 flex flex-wrap justify-center items-center gap-3">
            {WORKFLOW.map((step, i) => (
              <FadeUp key={step} delay={i * 0.05}>
                <div className="flex items-center gap-3">
                  <div className="border border-slate-200 bg-slate-50 px-5 py-3 font-semibold text-slate-800 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center bg-[#0B3D91] text-white text-xs font-bold">{i + 1}</span>
                    {step}
                  </div>
                  {i < WORKFLOW.length - 1 && <ChevronRight className="h-5 w-5 text-[#16A34A]" />}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="py-20 lg:py-28 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#16A34A]">Built For</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={JK}>One platform. Every receivables business.</h2>
            <p className="mt-4 text-slate-400 max-w-2xl">No separate products. Custom fields and reports adapt RecoverOS to your industry.</p>
          </FadeUp>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <FadeUp key={ind.label} delay={i * 0.03}>
                <div className="border border-white/10 bg-white/5 p-6 hover:bg-white/10 hover:border-[#16A34A]/50 transition-colors h-full">
                  <ind.icon className="h-7 w-7 text-[#16A34A]" />
                  <div className="mt-4 font-semibold" style={JK}>{ind.label}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Features</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950 max-w-2xl" style={JK}>Everything your recovery team needs</h2>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.label} delay={(i % 3) * 0.05}>
                <div className="bg-white p-7 h-full hover:bg-slate-50 transition-colors group">
                  <div className="h-10 w-10 bg-[#0B3D91]/10 flex items-center justify-center group-hover:bg-[#0B3D91] transition-colors">
                    <f.icon className="h-5 w-5 text-[#0B3D91] group-hover:text-white transition-colors" />
                  </div>
                  <div className="mt-4 font-semibold text-slate-900" style={JK}>{f.label}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BUSINESSES CHOOSE RECOVEROS */}
      <section className="py-20 lg:py-28 bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>Why RecoverOS</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Why Businesses Choose RecoverOS</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Built for recovery teams that need clarity and speed — not another heavy system to maintain.</p>
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="why-choose-grid">
            {WHY_CARDS.map((c, i) => (
              <FadeUp key={c.title} delay={(i % 4) * 0.05}>
                <div className="ros-feat rounded-xl border border-slate-200 bg-white p-6 h-full" data-testid={`why-card-${i}`}>
                  <div className="h-11 w-11 bg-[#16A34A]/10 flex items-center justify-center">
                    <c.icon className="h-5 w-5 text-[#16A34A]" />
                  </div>
                  <div className="mt-4 font-bold text-slate-900" style={JK}>{c.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WATCH BEFORE PRICING */}
      <section className="pt-20 lg:pt-28 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-center">
          <DemoThumbnail
            eyebrow="See it first"
            title="Watch RecoverOS in Action"
            subtitle="Not sure which plan fits? Watch the 4-minute demo, then pick a plan below."
            onStartTrial={() => appNav("/signup")}
            onBookDemo={() => openLead("demo")}
            source="pricing-section"
            className="mx-auto max-w-3xl"
          />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-28 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>Pricing</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Simple, transparent plans</h2>
            <p className="mt-3 text-slate-500">Start on Intro PAYG — ₹0 for 3 months. Scale anytime with credit-based plans and Free Monthly RC.</p>
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
            {plans.length === 0 ? (
              <div className="col-span-full flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" data-testid="pricing-loading" /></div>
            ) : plans.map((p, i) => {
              const isPayg = p.is_payg;
              const testKey = String(p.key || p.name).toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
              <FadeUp key={p.key || p.name} delay={i * 0.05} className={p.popular ? "lg:-my-3 z-10" : ""}>
                <div className={`relative bg-white border p-6 h-full flex flex-col ${p.popular ? "border-[#16A34A] border-2 shadow-2xl lg:scale-[1.03] ring-1 ring-[#16A34A]/20" : "border-slate-200"}`} data-testid={`pricing-card-${testKey}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 whitespace-nowrap">★ Recommended</span>}
                  <div className={`text-sm font-bold uppercase tracking-wider ${p.popular ? "text-[#16A34A]" : "text-slate-500"}`} style={JK}>{p.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className={`font-extrabold text-slate-950 ${p.popular ? "text-4xl" : "text-3xl"}`} style={JK} data-testid={`plan-price-${testKey}`}>{isPayg ? "₹0" : inr(p.price)}</span>
                    {!isPayg && <span className="text-sm text-slate-500">/mo</span>}
                  </div>
                  {isPayg
                    ? <div className="mt-1 text-[12px] font-semibold text-[#0B3D91]">3 months · new users</div>
                    : <div className="mt-1 text-[12px] text-slate-400">{p.annual_available === false ? "\u00A0" : `or ${inr(p.price_annual)}/yr`}</div>}
                  <div className="mt-4 flex flex-col gap-1.5">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Layers className="h-4 w-4 text-[#0B3D91]" /> {Number(p.account_limit).toLocaleString("en-IN")} Accounts</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Users className="h-4 w-4 text-[#0B3D91]" /> {p.user_limit} {p.user_limit === 1 ? "User" : "Users"}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16A34A]" data-testid={`plan-freerc-${testKey}`}><Gift className="h-4 w-4" /> Up to {p.free_rc_monthly} Free RC/mo</span>
                    {isPayg && <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500" data-testid="plan-emailcomms-payg"><Mail className="h-3.5 w-3.5" /> Up to 250 email communications</span>}
                  </div>
                  <ul className="mt-4 space-y-2 flex-1 border-t border-slate-100 pt-4">
                    {(p.features || []).slice(0, 4).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-[#16A34A] shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => appNav(`/signup?plan=${encodeURIComponent(p.key)}&billing=monthly`)} className={`mt-6 w-full rounded-none px-4 py-3 font-semibold transition-colors ${p.popular ? "bg-[#16A34A] text-white hover:bg-[#138a3e]" : "bg-[#0B3D91] text-white hover:bg-[#0a347d]"}`} data-testid={`plan-${testKey}`}>
                    {p.cta || (isPayg ? "Start PAYG" : "Get Started")}
                  </button>
                </div>
              </FadeUp>
              );
            })}
          </div>
          {/* View full pricing */}
          <FadeUp delay={0.06}>
            <div className="mt-8 text-center">
              <button onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 text-[#0B3D91] font-semibold hover:gap-3 transition-all" data-testid="view-full-pricing">
                View Full Pricing — Monthly/Annual, RC packs &amp; rates <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </FadeUp>
          {/* Add-ons note */}
          <FadeUp delay={0.08}>
            <div className="mt-6 bg-slate-950 text-white p-8 lg:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="addons-note">
              <div>
                <div className="text-sm font-bold uppercase tracking-wider text-[#16A34A]" style={JK}>Need More Accounts or Users?</div>
                <div className="mt-2 text-2xl font-extrabold" style={JK}>Buy Account & User Add-ons anytime</div>
                <p className="mt-1 text-slate-400 text-sm">Increase your limits instantly without changing your plan. From +250 accounts (₹299/mo) or +1 user (₹499/mo).</p>
              </div>
              <button onClick={() => appNav("/signup")} className="bg-white text-slate-950 rounded-none px-6 py-3.5 font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap" data-testid="addons-cta">Get Started</button>
            </div>
          </FadeUp>
          {/* Assurances */}
          <FadeUp delay={0.1}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="plan-assurances">
              {PLAN_ASSURANCES.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700"><Check className="h-4 w-4 text-[#16A34A]" /> {a}</span>
              ))}
            </div>
          </FadeUp>
          {/* Secure payments + accepted methods */}
          <FadeUp delay={0.1}>
            <div className="mt-10">
              <RazorpaySecureBar />
            </div>
          </FadeUp>

          {/* Trust & security assurances */}
          <FadeUp delay={0.12}>
            <div className="mt-6">
              <TrustSection />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PARTNER PROGRAM */}
      <section id="partners" className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <Overline>Partner Program</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Partner With RecoverOS</h2>
            <p className="mt-4 text-slate-600">Refer clients and earn recurring rewards. Perfect for professionals working with businesses that manage receivables.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {PARTNER_AUDIENCE.map((a) => (
                <span key={a} className="border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700">{a}</span>
              ))}
            </div>
            <button onClick={() => openLead("partner")} className="mt-8 bg-[#16A34A] text-white rounded-none px-6 py-3.5 font-semibold hover:bg-[#138a3e] transition-colors flex items-center gap-2" data-testid="partner-cta">
              Become A Partner <ArrowRight className="h-4 w-4" />
            </button>
          </FadeUp>
          <FadeUp delay={0.1} className="grid sm:grid-cols-2 gap-4">
            {PARTNER_BENEFITS.map((b) => (
              <div key={b} className="border border-slate-200 p-6 hover:border-[#16A34A] transition-colors">
                <Check className="h-6 w-6 text-[#16A34A]" />
                <div className="mt-3 font-semibold text-slate-900 text-sm" style={JK}>{b}</div>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="py-20 lg:py-28 bg-[#0B3D91] text-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <FadeUp>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#a7f3c4]">Proven In The Field</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={JK}>Built & Tested In Real Collections Operations</h2>
            <p className="mt-6 text-lg text-blue-100 leading-relaxed">
              RecoverOS was initially built and battle-tested inside a live lending environment to manage receivables, settlements, follow-ups and recoveries at scale — before becoming the platform it is today.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>FAQ</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Frequently Asked Questions</h2>
            <p className="mt-4 text-slate-600">Everything you need to know before you start.</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-12 space-y-3" data-testid="faq-section">
              {FAQS.map((f, i) => (<FaqItem key={f.q} q={f.q} a={f.a} idx={i} />))}
            </div>
            <div className="mt-8 text-center text-sm text-slate-600">
              Still have questions?{" "}
              <button onClick={() => openLead("contact")} className="font-semibold text-[#0B3D91] hover:underline" data-testid="faq-contact">Contact us</button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* TRIAL CTA */}
      <section className="py-20 lg:py-28 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <FadeUp>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950" style={JK}>Start with Intro PAYG — ₹0 for 3 months</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3">
              {["No subscription required for 3 months", "Up to 100 accounts · 1 user", "Up to 250 Email Communications", "Includes up to 50 Free RC/month"].map((b) => (
                <span key={b} className="flex items-center gap-2 text-slate-700 font-medium"><Check className="h-5 w-5 text-[#16A34A]" />{b}</span>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button onClick={() => appNav("/signup")} className="bg-[#16A34A] text-white rounded-none px-8 py-4 font-semibold hover:bg-[#138a3e] transition-colors" data-testid="cta-trial">Start with Intro PAYG</button>
              <button onClick={() => openLead("demo")} className="border-2 border-slate-300 text-slate-900 rounded-none px-8 py-4 font-semibold hover:border-[#0B3D91] hover:text-[#0B3D91] transition-colors" data-testid="cta-demo">Book Demo</button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-white pt-20 pb-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.4fr_1fr_1fr] gap-12">
            <div>
              <div className="flex items-center gap-2">
                <Brand dark iconSize={34} textClass="text-2xl" style={JK} />
              </div>
              <p className="mt-4 text-slate-400 max-w-sm">B2B Receivables Management Software.</p>
              <p className="mt-4 text-lg font-semibold text-slate-200 leading-snug" style={JK}>
                Track Outstanding Amounts.<br />Manage Follow-Ups.<br />Close More Recoveries.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500" style={JK}>Company</h4>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li><button onClick={() => openLead("contact")} className="hover:text-[#16A34A] transition-colors" data-testid="footer-contact">Contact Us</button></li>
                <li><button onClick={() => openLead("demo")} className="hover:text-[#16A34A] transition-colors" data-testid="footer-demo">Book Demo</button></li>
                <li><button onClick={() => openLead("partner")} className="hover:text-[#16A34A] transition-colors" data-testid="footer-partner">Partner Program</button></li>
                <li><a href="#features" className="hover:text-[#16A34A] transition-colors">Features</a></li>
                <li><button onClick={() => navigate("/pricing")} className="hover:text-[#16A34A] transition-colors" data-testid="footer-pricing">Pricing</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500" style={JK}>Get In Touch</h4>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li><button onClick={() => window.dispatchEvent(new Event("recoveros:open-chat"))} className="flex items-center gap-2 hover:text-white" data-testid="footer-chat"><MessageCircle className="h-4 w-4 text-[#16A34A]" />Chat with RecoverOS <span className="text-[10px] font-semibold text-[#16A34A]">24×7</span></button></li>
                <li><button onClick={() => openLead("demo")} className="flex items-center gap-2 hover:text-white" data-testid="footer-getintouch-demo"><CalendarCheck className="h-4 w-4 text-[#16A34A]" />Book a demo</button></li>
                <li><button onClick={() => openLead("contact")} className="flex items-center gap-2 hover:text-white" data-testid="footer-callback"><PhoneCall className="h-4 w-4 text-[#16A34A]" />Request a callback</button></li>
                <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#16A34A]" /><a href="mailto:support@recoveros.in" className="hover:text-white">support@recoveros.in</a></li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#16A34A]" />New Delhi, India</li>
                <li className="flex items-center gap-2"><Building className="h-4 w-4 text-[#16A34A]" />Trustera Services</li>
              </ul>

              {(() => {
                const socials = [
                  { name: "LinkedIn", href: "https://www.linkedin.com/company/recoveros", Icon: Linkedin },
                  { name: "YouTube", href: "https://www.youtube.com/@RecoverOS", Icon: Youtube },
                  { name: "Facebook", href: "https://www.facebook.com/RecoverOS", Icon: Facebook },
                  { name: "Instagram", href: "https://www.instagram.com/recoveros", Icon: Instagram },
                ].filter((s) => s.href && s.href.trim());
                if (!socials.length) return null;
                return (
                  <div className="mt-6" data-testid="footer-social">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500" style={JK}>Connect With Us</h4>
                    <div className="mt-3 flex items-center gap-3">
                      {socials.map(({ name, href, Icon }) => (
                        <a
                          key={name}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`RecoverOS on ${name}`}
                          title={name}
                          data-testid={`footer-social-${name.toLowerCase()}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-white/10 text-slate-300 hover:text-white hover:border-[#16A34A] hover:bg-[#16A34A]/10 transition-colors"
                        >
                          <Icon className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <SecureBadge />
              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
                <a href="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a>
                <a href="/terms-of-service" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</a>
                <a href="/security" className="hover:text-white transition-colors" data-testid="footer-security">Security</a>
                <button onClick={() => openLead("contact")} className="hover:text-white transition-colors" data-testid="footer-contact-2">Contact Us</button>
              </div>
            </div>
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} RecoverOS · Trustera Services. All rights reserved. RecoverOS is a B2B SaaS software platform operated by Trustera Services. RecoverOS provides receivables management technology and does not provide lending, debt collection or debt-settlement services.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
